import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Activity, Cpu, RefreshCw, Zap, Shield, AlertTriangle, 
  CheckCircle, ArrowRightLeft, FileText, Users, Clock, Settings, Download
} from 'lucide-react';
import { useSocket } from '../context/SocketContext';
import { toast } from 'sonner';
import WinResilienceMonitor from '../components/WinResilienceMonitor';
import MicroinvestOcrTab from '../components/MicroinvestOcrTab';
import N8nExecutionsMonitor from '../components/N8nExecutionsMonitor';

export default function OpenBalancerDashboard() {
  const { socket } = useSocket();
  const [activeTab, setActiveTab] = useState('balancer');
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [switching, setSwitching] = useState(false);
  const [executingHandoff, setExecutingHandoff] = useState(false);
  const [threshold, setThreshold] = useState(10);
  const [selectedHandoff, setSelectedHandoff] = useState(null);


  const fetchStatus = async (retries = 3) => {
    try {
      setLoading(true);
      let lastError = null;
      for (let attempt = 0; attempt < retries; attempt++) {
        try {
          const res = await fetch('/api/openbalancer/status');
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const json = await res.json();
          if (json.success && json.data) {
            setStatus(json.data);
            if (json.data.threshold !== undefined) {
              setThreshold(json.data.threshold);
            }
            return; // Success — exit
          }
        } catch (err) {
          lastError = err;
          if (attempt < retries - 1) {
            await new Promise(r => setTimeout(r, 500 * Math.pow(2, attempt)));
          }
        }
      }
      // Only show error after all retries exhausted
      if (lastError) {
        toast.error('Failed to fetch OpenBalancer status: ' + lastError.message);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();

    if (socket) {
      socket.on('usage:update', (data) => {
        setStatus((prev) => ({ ...prev, ...data.current }));
      });

      socket.on('model:switched', (entry) => {
        toast.info(`Model Switched: ${entry.from} → ${entry.to} (${entry.reason})`);
        fetchStatus();
      });

      socket.on('handoff:complete', (log) => {
        toast.success(`Agent Compaction Handoff Complete! (${log.agentsProcessed} agents processed)`);
        fetchStatus();
      });

      return () => {
        socket.off('usage:update');
        socket.off('model:switched');
        socket.off('handoff:complete');
      };
    }
  }, [socket]);

  const handleModelSwitch = async (targetModel) => {
    try {
      setSwitching(true);
      const res = await fetch('/api/openbalancer/switch-model', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ targetModel, reason: 'user-dashboard-action' })
      });
      const json = await res.json();
      if (json.success) {
        toast.success(`Switched model to ${targetModel === 'primary' ? 'Gemini 3.6 Flash' : 'Claude Opus 4.6'}`);
        fetchStatus();
      } else {
        toast.error(json.error || 'Failed to switch model');
      }
    } catch (err) {
      toast.error('Error switching model: ' + err.message);
    } finally {
      setSwitching(false);
    }
  };

  const handleUpdateThreshold = async (newVal) => {
    try {
      const res = await fetch('/api/openbalancer/threshold', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ threshold: Number(newVal) })
      });
      const json = await res.json();
      if (json.success) {
        setThreshold(Number(newVal));
        toast.success(`Auto-switch threshold updated to ${newVal}%`);
      }
    } catch (err) {
      toast.error('Failed to update threshold: ' + err.message);
    }
  };

  const handleTriggerHandoff = async () => {
    try {
      setExecutingHandoff(true);
      const res = await fetch('/api/openbalancer/handoff', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: 'user-manual-compact' })
      });
      const json = await res.json();
      if (json.success) {
        toast.success(`Agent Compaction Handoff complete! Log saved to ${json.data.handoffPath}`);
        fetchStatus();
      } else {
        toast.error(json.error || 'Handoff failed');
      }
    } catch (err) {
      toast.error('Handoff execution failed: ' + err.message);
    } finally {
      setExecutingHandoff(false);
    }
  };

  const currentModelKey = status?.currentModelKey || 'primary';
  const currentModel = status?.currentModel || { name: 'Gemini 3.6 Flash (High)', tier: 'flash' };
  const members = status?.members ? Object.values(status.members) : [];
  const switchLog = status?.switchLog || [];
  const handoffHistory = status?.handoffHistory || [];

  return (
    <div className="space-y-6">
      {/* Top Header Banner */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 bg-dark-800/80 backdrop-blur-md p-6 rounded-2xl border border-dark-700 shadow-xl">
        <div className="flex items-center space-x-4">
          <div className="p-3 bg-gradient-to-tr from-cyan-500/20 to-emerald-500/20 border border-cyan-500/30 rounded-xl">
            <Zap className="w-8 h-8 text-cyan-400 animate-pulse" />
          </div>
          <div>
            <div className="flex items-center space-x-3">
              <h1 className="text-2xl font-bold text-white tracking-wide">OpenBalancer Control Center</h1>
              <span className="px-3 py-1 text-xs font-semibold rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-ping" />
                Live Load Balancer
              </span>
            </div>
            <p className="text-gray-400 text-sm mt-1">
              Automated Usage Monitoring • 5h Quota Load Balancing • Agent Compaction & Handoff System
            </p>
          </div>
        </div>

        <div className="flex items-center space-x-3">
          <button
            onClick={fetchStatus}
            disabled={loading}
            className="px-4 py-2.5 bg-dark-700 hover:bg-dark-600 text-gray-200 rounded-xl border border-dark-600 flex items-center space-x-2 transition-all"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>

          <button
            onClick={handleTriggerHandoff}
            disabled={executingHandoff}
            className="px-4 py-2.5 bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white font-medium rounded-xl shadow-lg shadow-amber-500/20 flex items-center space-x-2 transition-all"
          >
            <FileText className="w-4 h-4" />
            <span>{executingHandoff ? 'Compacting...' : 'Compact & Handoff'}</span>
          </button>
        </div>
      </div>

      {/* Top Navigation Tab Switcher */}
      <div className="flex items-center space-x-2 border-b border-dark-700 pb-2">
        <button
          onClick={() => setActiveTab('balancer')}
          className={`px-5 py-2.5 rounded-xl font-medium text-sm transition-all flex items-center space-x-2 ${
            activeTab === 'balancer'
              ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/40 shadow-lg shadow-cyan-500/10'
              : 'text-gray-400 hover:text-white hover:bg-dark-800'
          }`}
        >
          <Zap className="w-4 h-4 text-cyan-400" />
          <span>OpenBalancer Control Center</span>
        </button>

        <button
          onClick={() => setActiveTab('ocr')}
          className={`px-5 py-2.5 rounded-xl font-medium text-sm transition-all flex items-center space-x-2 ${
            activeTab === 'ocr'
              ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 shadow-lg shadow-emerald-500/10'
              : 'text-gray-400 hover:text-white hover:bg-dark-800'
          }`}
        >
          <FileText className="w-4 h-4 text-emerald-400" />
          <span>Microinvest OCR & AI Accountant</span>
        </button>
      </div>

      {/* Tab Content Display */}
      <AnimatePresence mode="wait">
        {activeTab === 'balancer' ? (
          <motion.div
            key="balancer"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="space-y-6"
          >
            {/* Active Model & Load Balancing Card */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Active Model Indicator */}
              <div className="bg-dark-800/80 backdrop-blur-md p-6 rounded-2xl border border-dark-700 shadow-xl flex flex-col justify-between">
                <div>
                  <span className="text-xs uppercase font-semibold text-gray-400 tracking-wider">Active Engine</span>
                  <div className="mt-3 flex items-center space-x-3">
                    <Cpu className="w-6 h-6 text-cyan-400" />
                    <h2 className="text-xl font-bold text-white">{currentModel.name}</h2>
                  </div>
                  <p className="text-xs text-gray-400 mt-2">
                    Auto-switches to Claude Opus 4.6 (Thinking) when member quota drops below <span className="text-amber-400 font-semibold">{threshold}%</span>.
                  </p>
                </div>

                <div className="mt-6 pt-4 border-t border-dark-700 flex items-center justify-between">
                  <span className="text-xs text-gray-400">Manual Switch:</span>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleModelSwitch('primary')}
                      disabled={switching || currentModelKey === 'primary'}
                      className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all ${
                        currentModelKey === 'primary'
                          ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/40'
                          : 'bg-dark-700 text-gray-400 hover:text-white'
                      }`}
                    >
                      Gemini 3.6 Flash
                    </button>
                    <button
                      onClick={() => handleModelSwitch('secondary')}
                      disabled={switching || currentModelKey === 'secondary'}
                      className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all ${
                        currentModelKey === 'secondary'
                          ? 'bg-amber-500/20 text-amber-300 border border-amber-500/40'
                          : 'bg-dark-700 text-gray-400 hover:text-white'
                      }`}
                    >
                      Claude Opus 4.6
                    </button>
                  </div>
                </div>
              </div>

              {/* Auto-Switch Settings */}
              <div className="bg-dark-800/80 backdrop-blur-md p-6 rounded-2xl border border-dark-700 shadow-xl flex flex-col justify-between">
                <div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs uppercase font-semibold text-gray-400 tracking-wider">Auto-Switch Rules</span>
                    <Settings className="w-4 h-4 text-gray-400" />
                  </div>
                  <div className="mt-3 flex items-center justify-between">
                    <span className="text-sm font-medium text-white">5-Hour Quota Threshold</span>
                    <span className="text-lg font-bold text-amber-400">{threshold}%</span>
                  </div>
                  <p className="text-xs text-gray-400 mt-2">
                    When remaining quota falls below this threshold, agents compact context and switch engines automatically.
                  </p>
                </div>

                <div className="mt-4">
                  <input
                    type="range"
                    min="5"
                    max="50"
                    step="5"
                    value={threshold}
                    onChange={(e) => handleUpdateThreshold(e.target.value)}
                    className="w-full h-2 bg-dark-700 rounded-lg appearance-none cursor-pointer accent-cyan-400"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>5% (Aggressive)</span>
                    <span>10% (Default)</span>
                    <span>50% (Conservative)</span>
                  </div>
                </div>
              </div>

              {/* Handoff Status Summary */}
              <div className="bg-dark-800/80 backdrop-blur-md p-6 rounded-2xl border border-dark-700 shadow-xl flex flex-col justify-between">
                <div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs uppercase font-semibold text-gray-400 tracking-wider">Compaction Handoffs</span>
                    <Shield className="w-4 h-4 text-emerald-400" />
                  </div>
                  <div className="mt-3 flex items-center space-x-3">
                    <Activity className="w-6 h-6 text-emerald-400" />
                    <div>
                      <div className="text-2xl font-bold text-white">{handoffHistory.length}</div>
                      <div className="text-xs text-gray-400">Total Handoff Snapshots Preserved</div>
                    </div>
                  </div>
                </div>

                <div className="mt-4 text-xs text-gray-400">
                  {handoffHistory.length > 0 ? (
                    <span className="text-emerald-400">Latest Handoff: {new Date(handoffHistory[handoffHistory.length - 1].timestamp).toLocaleTimeString()}</span>
                  ) : (
                    <span>No handoffs triggered in current session.</span>
                  )}
                </div>
              </div>
            </div>

            {/* Member Quota Monitoring Cards */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-white flex items-center gap-2">
                  <Users className="w-5 h-5 text-cyan-400" />
                  <span>Member Quota Balancer (5-Hour Window)</span>
                </h2>
                <span className="text-xs text-gray-400">Updated every 15s</span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {members.map((member) => {
                  const isCritical = member.usagePercent <= threshold;
                  const isLow = member.usagePercent < 30 && !isCritical;

                  return (
                    <motion.div
                      key={member.name}
                      whileHover={{ y: -3 }}
                      className={`bg-dark-800/80 backdrop-blur-md p-5 rounded-2xl border ${
                        isCritical 
                          ? 'border-red-500/40 shadow-red-500/10' 
                          : isLow 
                          ? 'border-amber-500/40 shadow-amber-500/10' 
                          : 'border-dark-700 shadow-xl'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-xs font-mono font-semibold text-gray-300 truncate max-w-[140px]" title={member.name}>
                          {member.name}
                        </span>
                        <span className={`px-2 py-0.5 text-[10px] uppercase font-bold rounded-full border ${
                          isCritical
                            ? 'bg-red-500/10 text-red-400 border-red-500/20'
                            : isLow
                            ? 'bg-amber-500/10 text-amber-400 border-amber-500/20'
                            : 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
                        }`}>
                          {member.status}
                        </span>
                      </div>

                      <div className="flex items-baseline justify-between my-2">
                        <span className="text-3xl font-extrabold text-white">{member.usagePercent}%</span>
                        <span className="text-xs text-gray-400">{member.remainingMinutes}m left</span>
                      </div>

                      {/* Progress Bar */}
                      <div className="w-full h-2.5 bg-dark-700 rounded-full overflow-hidden my-3">
                        <div
                          className={`h-full rounded-full transition-all duration-500 ${
                            isCritical
                              ? 'bg-gradient-to-r from-red-500 to-rose-600'
                              : isLow
                              ? 'bg-gradient-to-r from-amber-400 to-orange-500'
                              : 'bg-gradient-to-r from-emerald-400 to-cyan-500'
                          }`}
                          style={{ width: `${member.usagePercent}%` }}
                        />
                      </div>

                      <div className="flex items-center justify-between text-[11px] text-gray-400 mt-2">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3 text-gray-500" />
                          <span>5h Rolling</span>
                        </span>
                        <span>{member.usedMinutes} / {member.totalMinutes} min</span>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>

            {/* Model Switching Audit Log & Recent Handoff Documents */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Model Switch Audit Log */}
              <div className="bg-dark-800/80 backdrop-blur-md p-6 rounded-2xl border border-dark-700 shadow-xl">
                <h3 className="text-base font-bold text-white mb-4 flex items-center gap-2">
                  <ArrowRightLeft className="w-4 h-4 text-cyan-400" />
                  <span>Model Switching Audit Trail</span>
                </h3>

                <div className="space-y-3 max-h-60 overflow-y-auto pr-2">
                  {switchLog.length === 0 ? (
                    <p className="text-sm text-gray-500 italic">No model switches recorded yet.</p>
                  ) : (
                    switchLog.slice().reverse().map((entry, idx) => (
                      <div key={idx} className="p-3 bg-dark-900/60 rounded-xl border border-dark-700/50 flex items-center justify-between text-xs">
                        <div>
                          <div className="font-semibold text-gray-200">
                            {entry.from} → <span className="text-cyan-400">{entry.to}</span>
                          </div>
                          <div className="text-gray-500 text-[11px] mt-0.5">
                            Reason: <span className="text-gray-400">{entry.reason}</span> • By: {entry.triggeredBy}
                          </div>
                        </div>
                        <span className="text-[10px] text-gray-500 font-mono">
                          {new Date(entry.timestamp).toLocaleTimeString()}
                        </span>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Handoff Snapshots Log */}
              <div className="bg-dark-800/80 backdrop-blur-md p-6 rounded-2xl border border-dark-700 shadow-xl">
                <h3 className="text-base font-bold text-white mb-4 flex items-center gap-2">
                  <FileText className="w-4 h-4 text-amber-400" />
                  <span>Compaction Handoff History</span>
                </h3>

                <div className="space-y-3 max-h-60 overflow-y-auto pr-2">
                  {handoffHistory.length === 0 ? (
                    <p className="text-sm text-gray-500 italic">No handoff documents generated yet.</p>
                  ) : (
                    handoffHistory.slice().reverse().map((log, idx) => (
                      <div 
                        key={idx} 
                        onClick={() => setSelectedHandoff(log)}
                        className="p-3 bg-dark-900/60 hover:bg-dark-700/50 cursor-pointer rounded-xl border border-dark-700/50 flex items-center justify-between text-xs transition-all"
                      >
                        <div>
                          <div className="font-semibold text-amber-300">
                            Handoff: {log.reason} ({log.agentsProcessed} agents)
                          </div>
                          <div className="text-gray-400 text-[11px] mt-0.5 truncate max-w-[280px]">
                            {log.handoffPath}
                          </div>
                        </div>
                        <span className="text-[10px] text-gray-500 font-mono">
                          {new Date(log.timestamp).toLocaleTimeString()}
                        </span>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>

            {/* n8n Workflow Executions & Watchdog Monitor */}
            <N8nExecutionsMonitor />

            {/* Windows 10 VM 50-Point Resilience Matrix Monitor Section */}
            <WinResilienceMonitor />
          </motion.div>
        ) : (
          <motion.div
            key="ocr"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
          >
            <MicroinvestOcrTab />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
